import { useState } from 'react';
import type { TableProps } from 'antd';
import { Drawer } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac } from '../../../components/shared';
import { Badge, Button, Dialog, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { ICON_KENH } from '../constants';
import { useBatTatKenh, useDanhMuc, useDanhSachKenh, useGuiThu, useXoaKenh } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { demTheoLoai, THU_TU_KENH } from '../nghiep-vu/canh-bao';
import type { MaKenh, NotifyChannel, NotifyChannelType, PhamViCanhBao } from '../types';
import { HopKenh } from './HopKenh';
import { HopXacNhan } from './HopXacNhan';
import { VungLoi } from './VungLoi';

type HopMo = {
  loai: NotifyChannelType;
  kenh: NotifyChannel | null;
  lan: number;
} | null;

/**
 * Tab "Cấu hình kênh" (aaPanel: Alarm Settings): một dòng mỗi loại kênh nền tảng cho phép, cột trạng thái nói đã có
 * bao nhiêu cấu hình. Bấm một loại mở ngăn liệt kê cấu hình của loại đó; thêm, sửa là hộp thoại dựng từ `fields`.
 */
export function CauHinhKenh({ pv, duocSua }: { pv: PhamViCanhBao; duocSua: boolean }) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const danhMuc = useDanhMuc(pv);
  const kenh = useDanhSachKenh(pv);
  const [ngan, setNgan] = useState<MaKenh | null>(null);
  const [huongDan, setHuongDan] = useState<MaKenh | null>(null);
  const [hop, setHop] = useState<HopMo>(null);

  const loaiKenh = [...(danhMuc.data?.channel_types ?? [])].sort(
    (a, b) => THU_TU_KENH.indexOf(a.code) - THU_TU_KENH.indexOf(b.code),
  );
  const theoLoai = demTheoLoai(kenh.data ?? []);
  const dangTai = danhMuc.isLoading || kenh.isLoading;
  const loi = danhMuc.error ?? kenh.error;
  const loaiNgan = loaiKenh.find((l) => l.code === ngan) ?? null;
  const moThem = (loai: NotifyChannelType) => setHop((h) => ({ loai, kenh: null, lan: (h?.lan ?? 0) + 1 }));

  const columns: TableProps<NotifyChannelType>['columns'] = [
    {
      title: t('canhBaoKenh.cotKenh'),
      render: (_v, row) => {
        const Icon = ICON_KENH[row.code];
        return (
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded bg-primary-50 text-primary-600">
              <Icon />
            </span>
            <div className="min-w-0">
              <Button variant="link" size="sm" className="!px-0 font-medium" onClick={() => setNgan(row.code)}>
                {dich.tenKenh(row.code)}
              </Button>
              <p className="text-xs text-content-muted">
                {dich.moKenh(row.code)}{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="!h-auto !px-0 text-xs"
                  onClick={() => setHuongDan(row.code)}
                >
                  {t('canhBaoKenh.xemHuongDan')}
                </Button>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: t('canhBaoKenh.cotCauHinh'),
      width: 200,
      render: (_v, row) => {
        const ds = theoLoai.get(row.code) ?? [];
        const tat = ds.filter((k) => !k.enabled).length;
        if (ds.length === 0) return <Badge variant="neutral">{t('canhBaoKenh.chuaCauHinh')}</Badge>;
        return (
          <span className="flex flex-wrap gap-1">
            <Badge variant="success">{t('canhBaoKenh.soCauHinh', { so: soVN(ds.length, locale) })}</Badge>
            {tat > 0 && <Badge variant="warning">{t('canhBaoKenh.soDangTat', { so: soVN(tat, locale) })}</Badge>}
          </span>
        );
      },
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={dich.tenKenh(row.code)}
          muc={[
            {
              khoa: 'mo',
              nhan: t('canhBaoKenh.moCauHinh', {
                kenh: dich.tenKenh(row.code),
              }),
              icon: <SettingOutlined />,
              onChon: () => setNgan(row.code),
            },
            {
              khoa: 'them',
              nhan: t('canhBaoKenh.them'),
              icon: <PlusOutlined />,
              hien: duocSua,
              onChon: () => moThem(row),
            },
            {
              khoa: 'huong-dan',
              nhan: t('canhBaoKenh.xemHuongDan'),
              icon: <QuestionCircleOutlined />,
              onChon: () => setHuongDan(row.code),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      {loi && !(danhMuc.data && kenh.data) ? (
        <VungLoi
          cau={t('canhBaoKenh.loiDanhSach', { loi: dichLoi(loi) })}
          onThuLai={() => {
            void danhMuc.refetch();
            void kenh.refetch();
          }}
        />
      ) : (
        <DataTable<NotifyChannelType>
          caption={t('canhBaoKenh.bang')}
          rowKey="code"
          dataSource={loaiKenh}
          columns={columns}
          loading={dangTai}
          size="middle"
          pagination={false}
          locale={{
            emptyText: dangTai ? (
              ' '
            ) : (
              <Empty
                icon={SettingOutlined}
                title={t('canhBaoKenh.rongLoai')}
                description={t('canhBaoKenh.rongLoaiMo')}
              />
            ),
          }}
        />
      )}

      <NganKenh
        pv={pv}
        loai={loaiNgan}
        ds={loaiNgan ? (theoLoai.get(loaiNgan.code) ?? []) : []}
        dangTai={kenh.isLoading}
        duocSua={duocSua}
        onClose={() => setNgan(null)}
        onThem={moThem}
        onSua={(loai, k) => setHop((h) => ({ loai, kenh: k, lan: (h?.lan ?? 0) + 1 }))}
      />

      <Dialog
        open={huongDan !== null}
        onClose={() => setHuongDan(null)}
        title={huongDan ? t('canhBaoKenh.huongDanTieuDe', { kenh: dich.tenKenh(huongDan) }) : ''}
        width={520}
        footer={
          <div className="flex justify-end">
            <Button variant="outline" size="md" onClick={() => setHuongDan(null)}>
              {t('chung.dong')}
            </Button>
          </div>
        }
      >
        <p className="whitespace-pre-line pt-2 text-sm leading-relaxed text-content-secondary">
          {huongDan ? dich.huongDan(huongDan) : ''}
        </p>
      </Dialog>

      {hop && <HopKenh key={hop.lan} pv={pv} loaiKenh={hop.loai} dangCo={hop.kenh} onClose={() => setHop(null)} />}
    </div>
  );
}

/** Ngăn cấu hình của một loại kênh (aaPanel: hộp "… configuration" có nút Add và danh sách). */
function NganKenh({
  pv,
  loai,
  ds,
  dangTai,
  duocSua,
  onClose,
  onThem,
  onSua,
}: {
  pv: PhamViCanhBao;
  loai: NotifyChannelType | null;
  ds: NotifyChannel[];
  dangTai: boolean;
  duocSua: boolean;
  onClose: () => void;
  onThem: (loai: NotifyChannelType) => void;
  onSua: (loai: NotifyChannelType, k: NotifyChannel) => void;
}) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const guiThu = useGuiThu(pv);
  const batTat = useBatTatKenh(pv);
  const xoa = useXoaKenh(pv);
  const [xnGo, setXnGo] = useState<NotifyChannel | null>(null);
  const tenKenh = loai ? dich.tenKenh(loai.code) : '';

  return (
    <Drawer
      open={loai !== null}
      onClose={onClose}
      title={t('canhBaoKenh.nganTieuDe', { kenh: tenKenh })}
      placement="right"
      width={560}
      destroyOnHidden
      extra={
        duocSua && loai && ds.length > 0 ? (
          <Button variant="primary" size="sm" icon={<PlusOutlined />} onClick={() => onThem(loai)}>
            {t('canhBaoKenh.them')}
          </Button>
        ) : null
      }
    >
      {dangTai ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : ds.length === 0 ? (
        <Empty
          icon={SettingOutlined}
          title={t('canhBaoKenh.rongNgan', { kenh: tenKenh })}
          description={t('canhBaoKenh.rongNganMo', { kenh: tenKenh })}
          action={
            duocSua && loai ? (
              <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => onThem(loai)}>
                {t('canhBaoKenh.themDauTien')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="space-y-3">
          {ds.map((k) => (
            <li key={k.id} className="flex items-start gap-3 rounded-lg border border-default bg-surface px-4 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-content-primary" title={k.name}>
                    {k.name}
                  </span>
                  {k.enabled ? (
                    <Badge variant="success">{t('canhBaoKenh.dangBat')}</Badge>
                  ) : (
                    <Badge variant={k.disabled_reason === 'AUTO_FAILURES' ? 'danger' : 'neutral'}>
                      {k.disabled_reason === 'AUTO_FAILURES' ? t('canhBaoKenh.tuTat') : t('canhBaoKenh.dangTat')}
                    </Badge>
                  )}
                </div>
                <p className="break-words text-xs text-content-muted">
                  {!k.last_test_at
                    ? t('canhBaoKenh.chuaThu')
                    : k.last_test_ok
                      ? t('canhBaoKenh.thuDuocLuc', {
                          luc: ngayGioVN(k.last_test_at, locale),
                        })
                      : t('canhBaoKenh.thuHongLuc', {
                          luc: ngayGioVN(k.last_test_at, locale),
                          loi: k.last_test_error ?? '',
                        })}
                </p>
              </div>
              <MenuThaoTac
                tenBanGhi={k.name}
                muc={[
                  {
                    khoa: 'gui-thu',
                    nhan: t('canhBaoKenh.guiThu'),
                    icon: <ExperimentOutlined />,
                    hien: duocSua,
                    voHieu: guiThu.isPending,
                    onChon: () => guiThu.mutate(k),
                  },
                  {
                    khoa: 'sua',
                    nhan: t('canhBaoKenh.sua'),
                    icon: <EditOutlined />,
                    hien: duocSua && loai !== null,
                    onChon: () => loai && onSua(loai, k),
                  },
                  {
                    khoa: 'bat-tat',
                    nhan: k.enabled ? t('canhBaoKenh.tat') : t('canhBaoKenh.bat'),
                    icon: k.enabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
                    hien: duocSua,
                    onChon: () => batTat.mutate(k),
                  },
                  {
                    khoa: 'go',
                    nhan: t('canhBaoKenh.go'),
                    icon: <DeleteOutlined />,
                    nhom: 'pha-huy',
                    hien: duocSua,
                    onChon: () => {
                      xoa.reset();
                      setXnGo(k);
                    },
                  },
                ]}
              />
            </li>
          ))}
        </ul>
      )}
      <HopXacNhan
        open={xnGo !== null}
        tieuDe={t('canhBaoKenh.xnGoTieuDe', { ten: xnGo?.name ?? '' })}
        than={t('canhBaoKenh.xnGoThan')}
        nhanXacNhan={t('canhBaoKenh.go')}
        nhanDangChay={t('canhBaoKenh.dangGo')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onClose={() => setXnGo(null)}
        onXacNhan={() => xnGo && xoa.mutate(xnGo, { onSuccess: () => setXnGo(null) })}
      />
    </Drawer>
  );
}
